<template lang="pug">
.ttip(ref="content" @mouseover="onMouseOver" @mouseout="onMouseOut")
  .content(@click="onContentClick")
    slot
      .default-icon
        Icon(name="info" fill="#a2a2a2")
  .tip(ref="tip" role="tooltip" :class="[theme && theme]" @click="onTooltipClick")
    .arrow(ref="arrow" data-popper-arrow="")
    slot(name="tooltip")
</template>
<script setup lang="ts">
import { onBeforeUnmount, ref, useTemplateRef } from 'vue'

type PopperInstance = {
  destroy: () => void
  update: () => Promise<any>
}

const props = withDefaults(defineProps<{
  placement?: 'top' | 'bottom' | 'left' | 'right'
  manual?: boolean
  mode?: 'hover' | 'click'
  theme?: string | null
  offset?: [number, number]
}>(), {
  placement : 'top',
  manual    : false,
  mode      : 'hover',
  theme     : null,
  offset    : () => [0, 15],
})

const contentRef = useTemplateRef<HTMLDivElement | null>('content')
const tipRef = useTemplateRef<HTMLDivElement | null>('tip')
const arrowRef = useTemplateRef<HTMLDivElement | null>('arrow')
const popperInstance = ref<PopperInstance | null>(null)

let popperCreator: any = null
async function getPopper() {
  if (popperCreator) return popperCreator
  const { createPopper } = await import(/* webpackChunkName: "popper" */ '@popperjs/core')
  popperCreator = createPopper

  return popperCreator
}

// Module-level active tip tracking — closing the previous tooltip when a new one opens,
// and closing on any outside click (matches old Vue 2 behaviour)
let activeTip: (() => void) | null = null

if (typeof window !== 'undefined') {
  const closeActiveTip = () => {
    if (activeTip) {
      activeTip()
      activeTip = null
    }
  }
  window.addEventListener('click', closeActiveTip)
  window.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeActiveTip() })
}

function destroy() {
  if (popperInstance.value) {
    tipRef.value?.classList.remove('show')
    popperInstance.value.destroy()
    popperInstance.value = null
    if (activeTip === destroy) {
      activeTip = null
    }
  }
}

function hover(val: boolean) {
  if (val && !popperInstance.value) {
    toggle()
  }
  if (!val && popperInstance.value) {
    destroy()
  }
}

async function toggle() {
  if (popperInstance.value) {
    destroy()

    return
  }

  if (activeTip) {
    activeTip()
  }

  const opts = {
    modifiers: [
      { name: 'offset', options: { offset: props.offset } },
      { name: 'arrow', options: { element: arrowRef.value } },
    ],
    placement: props.placement,
  }

  const createPopper = await getPopper()

  tipRef.value?.classList.add('show')
  popperInstance.value = createPopper(contentRef.value!, tipRef.value!, opts)
  activeTip = destroy
}

function onMouseOver() {
  if (props.mode !== 'click' && !props.manual) {
    hover(true)
  }
}

function onMouseOut() {
  if (props.mode !== 'click' && !props.manual) {
    hover(false)
  }
}

function onContentClick(e: Event) {
  if (!props.manual) {
    e.stopPropagation()
    e.preventDefault()
    toggle()
  }
}

function onTooltipClick(e: Event) {
  if (!props.manual) {
    e.stopPropagation()
  }
}

onBeforeUnmount(() => {
  destroy()
})

defineExpose({
  toggle,
  hover,
  destroy,
})
</script>
<style lang="scss" scoped>
  .ttip {
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  .arrow,
  .arrow::before {
    position: absolute;
    transform: scale(2);
    width: 8px;
    height: 8px;
    z-index: -1;
  }

  .tip[data-popper-placement^='top'] > .arrow {
    bottom: -4px;
  }

  .tip[data-popper-placement^='bottom'] > .arrow {
    top: -4px;
  }

  .tip[data-popper-placement^='left'] > .arrow {
    right: -4px;
  }

  .tip[data-popper-placement^='right'] > .arrow {
    left: -4px;
  }

  .arrow::before {
    z-index: -1;
    content: '';
    transform: rotate(45deg);
    background: $color-gray-light;
  }

  .default-icon {
    padding: $space-xxs;
    cursor: pointer;

    .icon {
      width: 20px;
      height: 20px;
    }
  }

  .tip {
    display: none;
    z-index: 999;
    background-color: $color-gray-light;
    color: $color-black;
    font-size: $size-xxs;
    max-width: 250px;
    padding: $space-xs;
    border-radius: 3px;
    -webkit-filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.5));
    filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.5));
    -ms-filter: "progid:DXImageTransform.Microsoft.Dropshadow(OffX=0, OffY=2, Color='#999')";
    filter: "progid:DXImageTransform.Microsoft.Dropshadow(OffX=0, OffY=2, Color='#999')";

    &.custom {
      padding: 0;
    }

    &.show {
      display: block;
    }
  }
</style>
<style lang="scss">
  .ttip .tip p:last-child {
    margin-bottom: 0;
  }

  .rhpa-content .tip {
    ol, ul, li, p {
      font-size: $size-xxs;
    }
  }
</style>
